import { RoseMark } from '@inboundx/shared/ui';
import { Link } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { useKnowledgeLearning } from '@/hooks/useKnowledgeLearning';

/**
 * Knowledge-ingestion banner for the Knowledge hub and app layout.
 *
 * While Rose is reading the site (or refreshing its knowledge for any other
 * reason) it shows the live progress; once nothing is running it collapses to
 * the single line saying own sources are optional.
 */
export function KbIngestCard({ variant = 'page' }: { variant?: 'page' | 'platform' }) {
    const {
        learning,
        title,
        label,
        detail,
        percent,
        processed,
        total,
        failed,
        unit,
        isWebsiteCrawl,
    } = useKnowledgeLearning();

    if (!learning) {
        if (variant === 'platform') return null;
        return (
            <p className="text-sm text-muted-foreground">
                Adding your own sources below is optional.
            </p>
        );
    }

    const hasCounts = processed != null && total != null && total > 0;

    return (
        <output
            className={
                variant === 'platform'
                    ? 'block shrink-0 border-b border-border-subtle bg-surface-sunken'
                    : 'block rounded-xl border border-border-subtle bg-surface-sunken p-4'
            }
        >
            <PageContainer
                layout={variant === 'platform' ? 'centered' : 'bleed'}
                className={variant === 'platform' ? 'py-3' : undefined}
            >
                <div className="flex items-center gap-3">
                    <span className="flex size-8 flex-none items-center justify-center rounded-lg bg-accent text-accent-foreground">
                        <RoseMark className="size-4 motion-safe:animate-[spin_3s_linear_infinite]" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-foreground">
                            Rose is learning…
                        </div>
                        {variant === 'page' && (
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                {/* What the running step does, not the job in general: a
                            refresh is past page selection once it fetches. */}
                                {detail ?? title ?? 'Refreshing agent knowledge.'}
                            </div>
                        )}
                    </div>
                    {/* Only the bar's own number goes next to the bar; the step
                    counter it is computed from goes in the phase line. */}
                    {percent != null ? (
                        <span className="flex-none text-xs text-muted-foreground tabular-nums">
                            {percent}%
                        </span>
                    ) : null}
                    {variant === 'platform' && (
                        <Link
                            to="/config-studio/data-sources"
                            className="flex-none rounded text-xs font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                        >
                            View knowledge
                        </Link>
                    )}
                </div>
                <div
                    className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border"
                    role="progressbar"
                    aria-label="Knowledge ingestion progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent ?? undefined}
                >
                    <div
                        className={`h-full rounded-full bg-accent-foreground ${percent == null ? 'progress-indeterminate' : 'motion-safe:transition-[width]'}`}
                        style={percent == null ? undefined : { width: `${Math.max(12, percent)}%` }}
                    />
                </div>
                {/* The counter is the part worth keeping when space runs out, so the
                phase text truncates around it rather than the whole line
                truncating and cutting the counter off the end. */}
                <div className="mt-2 flex items-baseline gap-1 text-xs text-muted-foreground">
                    <span className="truncate">
                        {(isWebsiteCrawl || variant === 'platform') && title ? `${title} · ` : ''}
                        {label ?? 'Starting…'}
                    </span>
                    {hasCounts ? (
                        <span className="flex-none tabular-nums">
                            · {processed}/{total} {unit}
                        </span>
                    ) : null}
                    {failed ? (
                        <span className="flex-none tabular-nums text-destructive">
                            · {failed} failed
                        </span>
                    ) : null}
                </div>
            </PageContainer>
        </output>
    );
}
